
import React, { useEffect, useRef, useState } from 'react';
import { MapPin, Navigation, Layers, Satellite, Map as MapIcon, Loader2, WifiOff, Crosshair } from 'lucide-react';

interface MapPickerProps {
  initialLat?: number;
  initialLng?: number;
  onLocationSelect: (lat: number, lng: number) => void;
  readOnly?: boolean;
}

export const MapPicker: React.FC<MapPickerProps> = ({ 
  initialLat, 
  initialLng, 
  onLocationSelect, 
  readOnly = false 
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  // Fix: replaced google.maps.Map with any to avoid namespace error
  const [map, setMap] = useState<any>(null);
  // Fix: replaced google.maps.Marker with any to avoid namespace error
  const [marker, setMarker] = useState<any>(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  // Fix: replaced google.maps.MapTypeId with string and used string literal 'roadmap' to avoid name error
  const [mapType, setMapType] = useState<string>('roadmap');
  const [isLocating, setIsLocating] = useState(false);
  const [coords, setCoords] = useState<{lat: number, lng: number} | null>(
    initialLat && initialLng ? { lat: initialLat, lng: initialLng } : null
  );

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    // Fix: access google through window to avoid 'Cannot find name google' error
    const google = (window as any).google;
    if (mapRef.current && isOnline && !map && google) {
      const defaultPos = { lat: initialLat || -15.7942, lng: initialLng || -47.8822 };
      
      const newMap = new google.maps.Map(mapRef.current, {
        center: defaultPos,
        zoom: 15,
        mapTypeId: mapType,
        disableDefaultUI: true,
        styles: [
          { elementType: "geometry", stylers: [{ color: "#242f3e" }] },
          { elementType: "labels.text.stroke", stylers: [{ color: "#242f3e" }] },
          { elementType: "labels.text.fill", stylers: [{ color: "#746855" }] },
          // Estilos para modo escuro
        ]
      });

      const newMarker = new google.maps.Marker({
        position: defaultPos,
        map: newMap,
        draggable: !readOnly,
        icon: {
          path: google.maps.SymbolPath.BACKWARD_CLOSED_ARROW,
          scale: 6,
          fillColor: "#0d9507",
          fillOpacity: 1,
          strokeWeight: 2,
          strokeColor: "#ffffff"
        }
      });

      if (!readOnly) {
        // Fix: changed event type to any to avoid google.maps namespace error
        newMap.addListener('click', (e: any) => {
          if (e.latLng) {
            const lat = e.latLng.lat();
            const lng = e.latLng.lng();
            newMarker.setPosition({ lat, lng });
            setCoords({ lat, lng });
            onLocationSelect(lat, lng);
          }
        });

        newMarker.addListener('dragend', () => {
          const pos = newMarker.getPosition();
          if (pos) {
            const lat = pos.lat();
            const lng = pos.lng();
            setCoords({ lat, lng });
            onLocationSelect(lat, lng);
          }
        });
      }

      setMap(newMap);
      setMarker(newMarker);
    }
  }, [isOnline, mapRef, initialLat, initialLng, readOnly, map]);

  useEffect(() => {
    if (map) {
      map.setMapTypeId(mapType);
    }
  }, [mapType, map]);

  const handleGetCurrentLocation = () => {
    setIsLocating(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          const pos = { lat: latitude, lng: longitude };
          setCoords(pos);
          onLocationSelect(latitude, longitude);
          
          if (map && marker) {
            map.setCenter(pos);
            map.setZoom(18);
            marker.setPosition(pos);
          }
          setIsLocating(false);
        },
        () => {
          alert("Não foi possível obter sua localização. Verifique as permissões de GPS.");
          setIsLocating(false);
        },
        { enableHighAccuracy: true }
      );
    }
  };

  const toggleMapType = () => {
    // Fix: used string literals for map types which are compatible with google.maps.MapTypeId
    const types = [
      'roadmap', 
      'satellite', 
      'hybrid',
      'terrain'
    ];
    const currentIndex = types.indexOf(mapType);
    const nextIndex = (currentIndex + 1) % types.length;
    setMapType(types[nextIndex]);
  };

  return (
    <div className="relative w-full h-64 rounded-3xl overflow-hidden border border-raizama-border/20 shadow-inner bg-raizama-bg">
      {/* Fallback Offline / No Internet */}
      {!isOnline && (
        <div className="absolute inset-0 z-10 bg-raizama-surface/90 flex flex-col items-center justify-center p-6 text-center">
          <WifiOff size={48} className="text-raizama-textSec mb-4 opacity-50" />
          <h4 className="text-white font-bold text-sm uppercase mb-1">Modo Offline Detectado</h4>
          <p className="text-[10px] text-raizama-textSec leading-relaxed">
            O GPS continua funcionando! Clique no botão de mira para capturar as coordenadas exatas do espécime mesmo sem internet.
          </p>
        </div>
      )}

      {/* Renderização do Mapa */}
      <div ref={mapRef} className="w-full h-full" />

      {/* Overlay de Coordenadas (Modo Campo) */}
      <div className="absolute top-3 left-3 z-20 flex flex-col gap-2">
        {coords && (
          <div className="bg-black/70 backdrop-blur-md px-3 py-1.5 rounded-xl border border-raizama-border/30 animate-scale-up">
            <p className="text-[9px] font-black text-raizama-action uppercase tracking-widest leading-none mb-1">Ponto Capturado</p>
            <p className="text-[10px] text-white font-mono font-bold">
              {coords.lat.toFixed(6)}, {coords.lng.toFixed(6)}
            </p>
          </div>
        )}
      </div>

      {/* Controles Flutuantes */}
      {!readOnly && (
        <div className="absolute bottom-4 right-4 z-20 flex flex-col gap-3">
          <button 
            type="button"
            onClick={toggleMapType}
            className="w-12 h-12 rounded-full bg-raizama-action text-white shadow-xl flex items-center justify-center hover:scale-105 active:scale-95 transition-all"
            title="Mudar Tipo de Mapa"
          >
            {/* Fix: use string comparison for map type check */}
            {mapType === 'satellite' ? <Satellite size={20} /> : <Layers size={20} />}
          </button>
          
          <button 
            type="button"
            onClick={handleGetCurrentLocation}
            disabled={isLocating}
            className="w-12 h-12 rounded-full bg-white text-raizama-action shadow-xl flex items-center justify-center hover:scale-105 active:scale-95 transition-all relative"
            title="Usar GPS do Celular"
          >
            {isLocating && (
              <div className="absolute inset-0 rounded-full bg-raizama-action animate-radar" />
            )}
            {isLocating ? <Loader2 size={20} className="animate-spin" /> : <Crosshair size={20} />}
          </button>
        </div>
      )}

      {/* Marcação Visual GPS Ativo */}
      {isLocating && (
        <div className="absolute inset-0 pointer-events-none flex items-center justify-center z-30">
          <div className="bg-raizama-action/20 border border-raizama-action/40 p-3 rounded-2xl backdrop-blur-sm">
             <span className="text-[9px] font-black text-white uppercase tracking-widest animate-pulse">Sintonizando Satélites...</span>
          </div>
        </div>
      )}
    </div>
  );
};
